import { API_ENDPOINTS } from '@/constants/api-endpoints';
import {
  acceptInvitation,
  addCredits,
  consumeCredits,
  createWorkspace,
  deleteWorkspace,
  demoteFromAdmin,
  generateJoinCode,
  getActiveWorkspace,
  getCreditAnalytics,
  getCreditHistory,
  getCreditTransactions,
  getWorkspaceById,
  getWorkspaceCredits,
  getWorkspaceInvitations,
  getWorkspaceMembers,
  getWorkspaces,
  joinWorkspace,
  planCreditConsumption,
  promoteToAdmin,
  purchaseCredits,
  removeMember,
  requestCreditAccess,
  resendInvitation,
  revokeInvitation,
  sendInvitation,
  switchWorkspace,
  updateMemberPermissions,
  updateWorkspace,
} from '@/services/workspaces';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ====================================================================
// Core Workspace Queries
// ====================================================================

export const useGetWorkspaces = (enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.listWorkspaces],
    queryFn: getWorkspaces,
    enabled,
  });
};

export const useGetActiveWorkspace = (enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getActiveWorkspace],
    queryFn: getActiveWorkspace,
    enabled,
  });
};

export const useGetWorkspaceById = (workspaceId: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getWorkspace(workspaceId)],
    queryFn: () => getWorkspaceById(workspaceId),
    enabled: enabled && !!workspaceId,
  });
};

// ====================================================================
// Core Workspace Mutations
// ====================================================================

export const useCreateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createWorkspace,
    onSuccess: () => {
      // Invalidate workspace list to refetch
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

export const useUpdateWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, data }: { workspaceId: number; data: any }) =>
      updateWorkspace(workspaceId, data),
    onSuccess: (_, variables) => {
      // Invalidate specific workspace and list
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

export const useDeleteWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

// ====================================================================
// Workspace Switching & Joining
// ====================================================================

export const useSwitchWorkspace = () => {
  return useMutation({
    mutationFn: switchWorkspace,
    // Remove onSuccess - let WorkspaceProvider handle comprehensive invalidation
    // This prevents duplicate invalidation and race conditions
  });
};

export const useJoinWorkspace = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: joinWorkspace,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

// ====================================================================
// Member Management
// ====================================================================

export const useGetWorkspaceMembers = (workspaceId: number, enabled = true) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getWorkspaceMembers(workspaceId)],
    queryFn: () => getWorkspaceMembers(workspaceId),
    enabled: enabled && !!workspaceId,
  });
};

export const useUpdateMemberPermissions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      workspaceId,
      memberId,
      data,
    }: {
      workspaceId: number;
      memberId: string;
      data: any;
    }) => updateMemberPermissions(workspaceId, memberId, data),
    onSuccess: (_, variables) => {
      // Invalidate members list to reflect changes
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      // CRITICAL: Invalidate active workspace to refresh current user's permissions
      // This prevents stale admin permissions after self-demotion
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
    },
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, memberId }: { workspaceId: number; memberId: string }) =>
      removeMember(workspaceId, memberId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
    },
  });
};

export const useRequestCreditAccess = () => {
  return useMutation({
    mutationFn: (workspaceId?: number) => requestCreditAccess(workspaceId),
  });
};

// ====================================================================
// Admin Management (Multi-Admin Feature)
// ====================================================================

export const usePromoteToAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, userId }: { workspaceId: number; userId: string }) =>
      promoteToAdmin(workspaceId, userId),
    onSuccess: (_, variables) => {
      // Invalidate members list to reflect admin status changes
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      // Invalidate active workspace to refresh current user's permissions if needed
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
      // Invalidate workspace details to refresh admin count
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
    },
  });
};

export const useDemoteFromAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workspaceId, userId }: { workspaceId: number; userId: string }) =>
      demoteFromAdmin(workspaceId, userId),
    onSuccess: (_, variables) => {
      // Invalidate members list to reflect admin status changes
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspaceMembers(variables.workspaceId)],
      });
      // CRITICAL: Invalidate active workspace to refresh current user's permissions
      // This prevents stale admin permissions after self-demotion
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
      // Invalidate workspace details to refresh admin count
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
      });
    },
  });
};

// ====================================================================
// Credit Management
// ====================================================================

export const useGetWorkspaceCredits = (workspaceId?: number) => {
  const key = workspaceId
    ? [API_ENDPOINTS.getWorkspaceCreditsById(workspaceId)]
    : [API_ENDPOINTS.getWorkspaceCredits];

  return useQuery({
    queryKey: key,
    queryFn: () => getWorkspaceCredits(workspaceId),
  });
};

export const usePlanCreditConsumption = () => {
  return useMutation({
    mutationFn: ({ creditsNeeded, workspaceId }: { creditsNeeded: number; workspaceId?: number }) =>
      planCreditConsumption(creditsNeeded, workspaceId),
  });
};

export const useConsumeCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      creditsNeeded,
      featureName,
      metadata,
      workspaceId,
    }: {
      creditsNeeded: number;
      featureName: string;
      metadata?: any;
      workspaceId?: number;
    }) => consumeCredits(creditsNeeded, featureName, metadata, workspaceId),
    onSuccess: (_, variables) => {
      // Invalidate credit queries
      const key = variables.workspaceId
        ? [API_ENDPOINTS.getWorkspaceCreditsById(variables.workspaceId)]
        : [API_ENDPOINTS.getWorkspaceCredits];
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

export const useAddCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      creditType,
      amount,
      description,
      workspaceId,
    }: {
      creditType: 'free' | 'paid';
      amount: number;
      description?: string;
      workspaceId?: number;
    }) => addCredits(creditType, amount, description, workspaceId),
    onSuccess: (_, variables) => {
      const key = variables.workspaceId
        ? [API_ENDPOINTS.getWorkspaceCreditsById(variables.workspaceId)]
        : [API_ENDPOINTS.getWorkspaceCredits];
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
};

export const useGetCreditHistory = (
  params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    creditType?: 'free' | 'paid';
    feature?: string;
  },
  workspaceId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getCreditHistory, params, workspaceId],
    queryFn: () => getCreditHistory(params, workspaceId),
    enabled,
  });
};

export const useGetCreditAnalytics = (
  params?: {
    startDate?: string;
    endDate?: string;
  },
  workspaceId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getCreditAnalytics, params, workspaceId],
    queryFn: () => getCreditAnalytics(params, workspaceId),
    enabled,
  });
};

export const useGetCreditTransactions = (
  params?: {
    page?: number;
    limit?: number;
    transactionType?: string;
  },
  workspaceId?: number,
  enabled = true,
) => {
  return useQuery({
    queryKey: [API_ENDPOINTS.getCreditTransactions, params, workspaceId],
    queryFn: () => getCreditTransactions(params, workspaceId),
    enabled,
  });
};

export const usePurchaseCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ packageId, workspaceId }: { packageId: string; workspaceId?: number }) =>
      purchaseCredits(packageId, workspaceId),
    onSuccess: (_, variables) => {
      // Invalidate credit queries for the specific workspace
      const key = variables.workspaceId
        ? [API_ENDPOINTS.getWorkspaceCreditsById(variables.workspaceId)]
        : [API_ENDPOINTS.getWorkspaceCredits];
      queryClient.invalidateQueries({ queryKey: key });

      // Invalidate active workspace to refresh credit balance in UI
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });

      // Invalidate all workspaces to refresh credit balance in workspace switcher
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.listWorkspaces],
      });

      // Invalidate workspace details
      if (variables.workspaceId) {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getWorkspace(variables.workspaceId)],
        });
      }
    },
  });
};

// ====================================================================
// Invitation Management
// ====================================================================

export const useSendInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data, workspaceId }: { data: any; workspaceId?: number }) =>
      sendInvitation(data, workspaceId),
    onSuccess: (_, variables) => {
      if (variables.workspaceId) {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getWorkspaceInvitations(variables.workspaceId)],
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.getInvitations],
        });
      }
    },
  });
};

export const useGetWorkspaceInvitations = (workspaceId?: number, enabled = true) => {
  const key = workspaceId
    ? [API_ENDPOINTS.getWorkspaceInvitations(workspaceId)]
    : [API_ENDPOINTS.getInvitations];

  return useQuery({
    queryKey: key,
    queryFn: () => getWorkspaceInvitations(workspaceId),
    enabled,
  });
};

export const useRevokeInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => {
      // Invalidate all invitation queries
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getInvitations],
      });
      // Also invalidate workspace-specific invitations
      queryClient.invalidateQueries({
        predicate: (query) =>
          !!(
            query.queryKey[0] &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('/workspaces/') &&
            query.queryKey[0].includes('/invitations')
          ),
      });
    },
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resendInvitation,
    onSuccess: () => {
      // Invalidate all invitation queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getInvitations],
      });
      // Also invalidate workspace-specific invitations
      queryClient.invalidateQueries({
        predicate: (query) =>
          !!(
            query.queryKey[0] &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('/workspaces/') &&
            query.queryKey[0].includes('/invitations')
          ),
      });
    },
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: acceptInvitation,
    onSuccess: () => {
      // Invalidate workspace lists since user joined a new workspace
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.listWorkspaces] });
      queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.getActiveWorkspace] });
    },
  });
};

// ====================================================================
// Join Code Management
// ====================================================================

export const useGenerateJoinCode = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateJoinCode,
    onSuccess: (_, workspaceId) => {
      // Invalidate workspace data to refresh the join code
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getWorkspace(workspaceId)],
      });
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.getActiveWorkspace],
      });
    },
  });
};
